/**
 * Folders React Query Hooks
 *
 * Provides React Query hooks for folders data fetching and mutations.
 * Used by Assets and potentially Templates for folder organization.
 */

import { useCallback } from 'react'
import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
  type UseMutationOptions,
} from '@tanstack/react-query'
import {
  foldersService,
  type FolderDto,
  type FolderHierarchyDto,
  type FolderTreeNode,
  type PaginatedFolderResponse,
  type CreateFolderRequest,
  type UpdateFolderRequest,
} from '@/lib/api/services/folders'

// ============================================
// Query Keys
// ============================================

export const foldersKeys = {
  all: ['folders'] as const,
  lists: () => [...foldersKeys.all, 'list'] as const,
  list: (params?: Record<string, unknown>) => [...foldersKeys.lists(), params] as const,
  tree: () => [...foldersKeys.all, 'tree'] as const,
  details: () => [...foldersKeys.all, 'detail'] as const,
  detail: (id: string) => [...foldersKeys.details(), id] as const,
}

// ============================================
// Query Hooks
// ============================================

/**
 * Hook to fetch folders with pagination and filtering.
 */
export function useFolders(
  options?: {
    parentId?: string | null
    searchTerm?: string
    sortBy?: string
    sortDescending?: boolean
    pageNumber?: number
    pageSize?: number
  },
  queryOptions?: Omit<UseQueryOptions<PaginatedFolderResponse, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: foldersKeys.list(options),
    queryFn: () => foldersService.getFolders(options),
    ...queryOptions,
  })
}

/**
 * Hook to fetch root folders (folders without a parent).
 */
export function useRootFolders(
  queryOptions?: Omit<UseQueryOptions<FolderDto[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: foldersKeys.list({ parentId: null }),
    queryFn: () => foldersService.getRootFolders(),
    ...queryOptions,
  })
}

/**
 * Hook to fetch the folder tree structure.
 */
export function useFolderTree(
  rootId?: string,
  maxDepth?: number,
  queryOptions?: Omit<UseQueryOptions<FolderTreeNode[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: foldersKeys.tree(),
    queryFn: () => foldersService.getFolderTree(rootId, maxDepth),
    ...queryOptions,
  })
}

/**
 * Hook to fetch a single folder with hierarchy information.
 */
export function useFolder(
  id: string,
  includeChildren = true,
  queryOptions?: Omit<UseQueryOptions<FolderHierarchyDto, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: foldersKeys.detail(id),
    queryFn: () => foldersService.getFolder(id, includeChildren),
    enabled: !!id,
    ...queryOptions,
  })
}

// ============================================
// Mutation Hooks
// ============================================

/**
 * Hook to create a new folder.
 */
export function useCreateFolder(
  options?: Omit<UseMutationOptions<FolderDto, Error, CreateFolderRequest, unknown>, 'mutationFn'>
) {
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: (data: CreateFolderRequest) => foldersService.createFolder(data),
    onSuccess: (newFolder, variables, context) => {
      // Invalidate folder queries
      queryClient.invalidateQueries({ queryKey: foldersKeys.all })

      // If the folder has a parent, invalidate that parent's detail query
      if (newFolder.parentId) {
        queryClient.invalidateQueries({
          queryKey: foldersKeys.detail(newFolder.parentId),
        })
      }

      // Call consumer's onSuccess if provided
      ;(options?.onSuccess as ((data: FolderDto, variables: CreateFolderRequest, context: unknown) => void) | undefined)?.(newFolder, variables, context)
    },
  })
}

/**
 * Hook to update an existing folder.
 */
type UpdateFolderVars = { id: string; data: UpdateFolderRequest }
export function useUpdateFolder(
  options?: Omit<UseMutationOptions<FolderDto, Error, UpdateFolderVars, unknown>, 'mutationFn'>
) {
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: ({ id, data }: UpdateFolderVars) => foldersService.updateFolder(id, data),
    onSuccess: (updatedFolder, variables, context) => {
      // Update the folder in cache
      queryClient.setQueryData(foldersKeys.detail(variables.id), (old: FolderHierarchyDto | undefined) => {
        if (!old) return old
        return { ...old, ...updatedFolder }
      })

      // Invalidate folder queries
      queryClient.invalidateQueries({ queryKey: foldersKeys.all })

      // Call consumer's onSuccess if provided
      ;(options?.onSuccess as ((data: FolderDto, variables: UpdateFolderVars, context: unknown) => void) | undefined)?.(updatedFolder, variables, context)
    },
  })
}

/**
 * Hook to delete a folder.
 */
export function useDeleteFolder(
  options?: Omit<UseMutationOptions<void, Error, { id: string; recursive?: boolean }, unknown>, 'mutationFn'>
) {
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: ({ id, recursive = false }: { id: string; recursive?: boolean }) =>
      foldersService.deleteFolder(id, recursive),
    onSuccess: (data, variables, context) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: foldersKeys.detail(variables.id) })

      // Invalidate folder queries
      queryClient.invalidateQueries({ queryKey: foldersKeys.all })

      // Call consumer's onSuccess if provided
      ;(options?.onSuccess as ((data: void, variables: { id: string; recursive?: boolean }, context: unknown) => void) | undefined)?.(data, variables, context)
    },
  })
}

/**
 * Hook to move a folder to a new parent.
 */
export function useMoveFolder(
  options?: Omit<UseMutationOptions<FolderDto, Error, { id: string; newParentId: string | null }, unknown>, 'mutationFn'>
) {
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: ({ id, newParentId }: { id: string; newParentId: string | null }) =>
      newParentId ? foldersService.moveFolder(id, newParentId) : foldersService.moveFolderToRoot(id),
    onSuccess: (data, variables, context) => {
      // Invalidate folder queries since the tree structure changed
      queryClient.invalidateQueries({ queryKey: foldersKeys.all })

      // Call consumer's onSuccess if provided
      ;(options?.onSuccess as ((data: FolderDto, variables: { id: string; newParentId: string | null }, context: unknown) => void) | undefined)?.(data, variables, context)
    },
  })
}

// ============================================
// Utility Hooks
// ============================================

/**
 * Hook to prefetch folder data (useful for hover states).
 */
export function usePrefetchFolder() {
  const queryClient = useQueryClient()

  return useCallback(
    (id: string) => {
      queryClient.prefetchQuery({
        queryKey: foldersKeys.detail(id),
        queryFn: () => foldersService.getFolder(id),
        staleTime: 1000 * 60 * 5, // 5 minutes
      })
    },
    [queryClient]
  )
}

/**
 * Hook to invalidate all folder cache.
 */
export function useInvalidateFolders() {
  const queryClient = useQueryClient()

  return useCallback(() => {
    queryClient.invalidateQueries({ queryKey: foldersKeys.all })
  }, [queryClient])
}

// Re-export types for convenience
export type {
  FolderDto,
  FolderHierarchyDto,
  FolderTreeNode,
  PaginatedFolderResponse,
  CreateFolderRequest,
  UpdateFolderRequest,
}
